import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarClock, ShieldCheck, CheckCircle2, CircleDashed } from "lucide-react";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import { mdy } from "@/lib/dateFmt";

/**
 * ADMIN-ONLY page for editing the SCHEDULE POST date on individual pay
 * periods or calendar months.  Managers already have offset + minimum rules
 * on the AvailabilityRulesPage — this is purely for one-off calendar
 * adjustments (holidays, month-end shifts, etc.).
 */
export default function SchedulePostingPage({ facilityId, facilityLabel }) {
  const [cadence, setCadence] = useState("biweekly");
  const [periods, setPeriods] = useState([]);
  const [pendingDue, setPendingDue] = useState({});

  const load = async () => {
    if (!facilityId) return;
    try {
      const [meta, p] = await Promise.all([
        api.get(`/facility-meta?facility_id=${facilityId}`),
        api.get(`/months?facility_id=${facilityId}`),
      ]);
      setCadence(meta.data?.cadence || "biweekly");
      setPeriods(p.data || []);
      setPendingDue({});
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [facilityId]);

  const upcoming = useMemo(
    () => periods.filter((p) => !p.is_past).slice(0, 12),
    [periods]
  );

  const saveDue = async (period_id, due_date) => {
    try {
      await api.put(`/period-due`, { facility_id: facilityId, period_id, due_date: due_date || null });
      toast.success(due_date ? "Custom schedule post date saved" : "Reverted to default");
      setPendingDue((s) => ({ ...s, [period_id]: undefined }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="schedule-posting-page">
      <div className="flex items-start gap-2 mb-1">
        <ShieldCheck size={18} className="text-amber-600 mt-0.5" />
        <div className="text-lg font-display font-semibold flex items-center gap-2">
          Schedule Posting Dates {facilityLabel && <span className="text-slate-400">· {facilityLabel}</span>}
        </div>
      </div>
      <div className="text-sm text-slate-500">
        {cadence === "monthly"
          ? "Schedule posts on the 15th of the month before each schedule month by default. Override any single month here for holidays or one-off adjustments."
          : "Schedule posts 7 days before each pay period starts by default. Override any single pay period here."}
      </div>

      <div className="mt-4 divide-y divide-slate-100">
        {upcoming.length === 0 && (
          <div className="text-sm text-slate-500 py-6 text-center">No upcoming periods.</div>
        )}
        {upcoming.map((p) => {
          const dv = pendingDue[p.id] !== undefined ? pendingDue[p.id] : (p.due_to_post || "");
          const today = new Date();
          const daysLeft = p.due_to_post ? differenceInCalendarDays(parseISO(p.due_to_post), today) : null;
          return (
            <div key={p.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`sched-post-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-sm">{p.label}</div>
                  {p.is_current && <span className="text-[10px] uppercase tracking-widest text-blue-600 font-bold">Current</span>}
                  {p.published ? (
                    <span className="inline-flex items-center gap-0.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 text-[9px] font-black uppercase tracking-widest px-1.5 py-[1px]">
                      <CheckCircle2 size={9} /> Published
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-0.5 rounded-full border border-slate-300 bg-slate-50 text-slate-600 text-[9px] font-black uppercase tracking-widest px-1.5 py-[1px]">
                      <CircleDashed size={9} /> Not Published
                    </span>
                  )}
                  {p.due_override && (
                    <span className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-bold">Custom</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 tabular-nums">
                  Due to post · {p.due_to_post ? mdy(p.due_to_post) : "—"}
                  {daysLeft != null && (
                    <span className={daysLeft < 0 ? "text-red-600 ml-1" : "ml-1"}>
                      ({daysLeft < 0 ? `${-daysLeft}d overdue` : daysLeft === 0 ? "due today" : `${daysLeft}d left`})
                    </span>
                  )}
                </div>
              </div>
              <Input
                type="date"
                value={dv || ""}
                onChange={(e) => setPendingDue((s) => ({ ...s, [p.id]: e.target.value }))}
                data-testid={`sched-post-input-${p.id}`}
                className="w-[180px]"
              />
              <div className="flex gap-1">
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => saveDue(p.id, pendingDue[p.id] !== undefined ? pendingDue[p.id] : p.due_to_post)}
                  data-testid={`sched-post-save-${p.id}`}
                >
                  Save
                </Button>
                {p.due_override && (
                  <Button size="sm" variant="outline"
                    onClick={() => saveDue(p.id, null)}
                    data-testid={`sched-post-reset-${p.id}`}
                  >
                    Revert
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
